import { useEffect, useState, Fragment } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Empty, Input, Select, Spinner, fmtBRL } from "@/components/ui/Primitives";
import Pagination from "@/components/ui/Pagination";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, X, Wallet, Trash2, RotateCcw, Pencil, Search } from "lucide-react";
import PricingAdjustmentModal from "@/components/PricingAdjustmentModal";

const METODOS = [
  ["pix", "PIX"],
  ["boleto", "Boleto"],
  ["transferencia", "Transferência"],
  ["dinheiro", "Dinheiro"],
  ["cheque", "Cheque"],
  ["cartao", "Cartão"],
  ["desconto", "Desconto (abatimento)"],
  ["outro", "Outro"],
];

const emptyAcc = { descricao: "", valor: "", data_vencimento: "", counterpart: "", categoria: "outros", observacoes: "" };

// Rótulos e cores para exibir a categoria de cada conta como badge na lista
const CAT_LABELS = {
  fornecedor: "Fornecedor",
  salario: "Salário",
  salario_adm: "Salários ADM",
  impostos: "Impostos",
  servico_beneficiamento: "Benef. Terceiros",
  venda: "Venda",
  frete: "Frete",
  sacaria: "Sacaria",
  energia: "Energia",
  aluguel: "Aluguel",
  folha: "Folha",
  manutencao: "Manutenção",
  materiais_escritorio: "Mat. Escritório",
  materiais_limpeza: "Mat. Limpeza",
  taxas_bancarias: "Taxas Bancárias",
  cesta_basica: "Cesta Básica",
  embalagem: "Embalagem",
  outros: "Outros",
};
const CAT_STYLES = {
  fornecedor: "bg-emerald-50 text-emerald-800 border-emerald-200",
  salario: "bg-sky-50 text-sky-800 border-sky-200",
  salario_adm: "bg-cyan-50 text-cyan-800 border-cyan-200",
  folha: "bg-sky-50 text-sky-800 border-sky-200",
  impostos: "bg-red-50 text-red-800 border-red-200",
  servico_beneficiamento: "bg-purple-50 text-purple-800 border-purple-200",
  venda: "bg-amber-50 text-amber-800 border-amber-200",
  frete: "bg-blue-50 text-blue-800 border-blue-200",
  sacaria: "bg-orange-50 text-orange-800 border-orange-200",
  energia: "bg-yellow-50 text-yellow-800 border-yellow-200",
  aluguel: "bg-indigo-50 text-indigo-800 border-indigo-200",
  manutencao: "bg-rose-50 text-rose-800 border-rose-200",
  materiais_escritorio: "bg-slate-50 text-slate-800 border-slate-200",
  materiais_limpeza: "bg-teal-50 text-teal-800 border-teal-200",
  taxas_bancarias: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  cesta_basica: "bg-lime-50 text-lime-800 border-lime-200",
  embalagem: "bg-orange-50 text-orange-800 border-orange-200",
  outros: "bg-stone-100 text-stone-700 border-stone-300",
};
const catLabel = (c) => CAT_LABELS[c] || (c ? c.replace(/_/g, " ") : "—");
const catStyle = (c) => CAT_STYLES[c] || "bg-stone-100 text-stone-700 border-stone-300";

export function AccountsPage({ kind }) {
  // kind = "payable" or "receivable"
  const isPay = kind === "payable";
  const url = isPay ? "/finance/accounts-payable" : "/finance/accounts-receivable";
  const parentType = isPay ? "accounts_payable" : "accounts_receivable";
  const title = isPay ? "Contas a Pagar" : "Contas a Receber";
  const subtitle = isPay ? "Obrigações com fornecedores e despesas" : "Recebimentos de clientes";
  const cpLabel = isPay ? "Fornecedor / Beneficiário" : "Cliente";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // all | open | overdue | paid
  const [categoria, setCategoria] = useState(""); // "" = todas
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [err, setErr] = useState("");
  const [openAcc, setOpenAcc] = useState(false);
  const [editingAcc, setEditingAcc] = useState(null);
  const [accForm, setAccForm] = useState(emptyAcc);
  const [busy, setBusy] = useState(false);

  const [paying, setPaying] = useState(null); // account being paid
  const [payForm, setPayForm] = useState({ valor: "", data_pagamento: new Date().toISOString().slice(0, 10), metodo: "pix", banco: "", observacoes: "" });
  const [pricingShipmentId, setPricingShipmentId] = useState(null); // abre modal de reajuste

  // Pagamento em lote
  const [selected, setSelected] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ data_pagamento: new Date().toISOString().slice(0, 10), metodo: "pix", banco: "", observacoes: "" });
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total, errors }

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filter, qDebounced, pageSize, kind, categoria]);
  // Reset seleção quando trocar filtro/página/kind
  useEffect(() => { setSelected(new Set()); }, [filter, qDebounced, page, pageSize, kind, categoria]);

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/finance/summary");
      setSummary(data);
    } catch (_) { /* ignore — não bloqueia listagem */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filter === "open") params.status = "aberta";
      if (filter === "paid") params.status = "paga";
      if (filter === "canceled") params.status = "cancelada";
      if (filter === "overdue") params.vencidas = true;
      if (qDebounced) params.q = qDebounced;
      if (categoria) params.categoria = categoria;
      const { data } = await api.get(url, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadSummary(); /* eslint-disable-next-line */ }, [filter, qDebounced, kind, page, pageSize, categoria]);

  // Cards globais (dataset inteiro, independem do filtro/paginação)
  const cards = (() => {
    if (!summary) return { open_value: 0, open_count: 0, overdue_value: 0, overdue_count: 0, paid_value: 0 };
    if (isPay) return {
      open_value: summary.ap_open || 0,
      open_count: summary.ap_count || 0,
      overdue_value: summary.ap_overdue || 0,
      overdue_count: summary.ap_overdue_count || 0,
      paid_value: summary.ap_paid || 0,
    };
    return {
      open_value: summary.ar_open || 0,
      open_count: summary.ar_count || 0,
      overdue_value: summary.ar_overdue || 0,
      overdue_count: summary.ar_overdue_count || 0,
      paid_value: summary.ar_paid || 0,
    };
  })();

  const refreshAll = async () => { await Promise.all([load(), loadSummary()]); };

  const openNew = () => { setEditingAcc(null); setAccForm(emptyAcc); setOpenAcc(true); setErr(""); };
  const openEditAcc = (acc) => {
    setEditingAcc(acc);
    setAccForm({
      descricao: acc.descricao || "",
      valor: acc.valor ?? "",
      data_vencimento: acc.data_vencimento || "",
      counterpart: acc.counterpart || "",
      categoria: acc.categoria || "outros",
      observacoes: acc.observacoes || "",
    });
    setOpenAcc(true); setErr("");
  };
  const submitAcc = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...accForm, valor: Number(accForm.valor) || 0 };
      if (editingAcc) {
        if (editingAcc.origem === "auto") {
          // Contas AUTO: PATCH restrito a valor/vencimento/descrição/observações
          await api.patch(`${url}/${editingAcc.id}/valor`, {
            valor: payload.valor,
            data_vencimento: payload.data_vencimento,
            descricao: payload.descricao,
            observacoes: payload.observacoes,
          });
        } else {
          await api.put(`${url}/${editingAcc.id}`, payload);
        }
      } else {
        await api.post(url, payload);
      }
      setOpenAcc(false); await refreshAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };
  const onDelete = async (acc) => {
    if (!window.confirm("Excluir esta conta?")) return;
    try { await api.delete(`${url}/${acc.id}`); await refreshAll(); }
    catch (e) { alert(formatErr(e)); }
  };

  const openPay = (acc) => {
    setPaying(acc);
    setPayForm({ valor: acc.saldo, data_pagamento: new Date().toISOString().slice(0, 10), metodo: "pix", banco: "", observacoes: "" });
    setErr("");
  };
  const submitPay = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    // Valida banco obrigatório para pix/transferencia
    if ((payForm.metodo === "pix" || payForm.metodo === "transferencia") && !payForm.banco) {
      setErr("Selecione o banco de saída/entrada."); setBusy(false); return;
    }
    try {
      await api.post("/payments", {
        parent_type: parentType,
        parent_id: paying.id,
        valor: Number(payForm.valor) || 0,
        data_pagamento: payForm.data_pagamento,
        metodo: payForm.metodo,
        banco: payForm.banco || "",
        observacoes: payForm.observacoes,
      });
      setPaying(null); await refreshAll();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const onEstornar = async (paymentId) => {
    if (!window.confirm("Estornar este pagamento?")) return;
    try { await api.delete(`/payments/${paymentId}`); await refreshAll(); if (paying) {
      const r = await api.get(`${url}/${paying.id}`); setPaying(r.data);
    }}
    catch (e) { alert(formatErr(e)); }
  };

  // Desfazer pagamento direto da linha (para contas pagas ou parciais)
  const onDesfazerPagamento = async (acc) => {
    setErr("");
    try {
      const r = await api.get(`${url}/${acc.id}`);
      const ativos = (r.data.payments || []).filter((p) => !p.estornado);
      if (ativos.length === 0) {
        alert("Nenhum pagamento ativo para desfazer.");
        return;
      }
      if (ativos.length === 1) {
        const p = ativos[0];
        if (!window.confirm(`Desfazer o pagamento de ${fmtBRL(p.valor)} feito em ${fmtDate(p.data_pagamento)} (${p.metodo})?`)) return;
        await api.delete(`/payments/${p.id}`);
        await refreshAll();
      } else {
        // Múltiplos pagamentos: abre o modal para escolher qual estornar
        setPaying(r.data);
        setPayForm({ valor: r.data.saldo, data_pagamento: new Date().toISOString().slice(0, 10), metodo: "pix", banco: "", observacoes: "" });
      }
    } catch (e) { alert(formatErr(e)); }
  };

  // ----- Seleção múltipla + pagamento em lote -----
  const isEligible = (a) => a.status !== "paga" && a.status !== "cancelada" && Number(a.saldo || 0) > 0;
  const eligibleItems = items.filter(isEligible);
  const allEligibleSelected = eligibleItems.length > 0 && eligibleItems.every((a) => selected.has(a.id));
  const selectedItems = items.filter((a) => selected.has(a.id));
  const selectedTotal = selectedItems.reduce((s, a) => s + Number(a.saldo || 0), 0);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allEligibleSelected) setSelected(new Set());
    else setSelected(new Set(eligibleItems.map((a) => a.id)));
  };

  const submitBulkPay = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) return;
    if ((bulkForm.metodo === "pix" || bulkForm.metodo === "transferencia") && !bulkForm.banco) {
      setErr(isPay ? "Selecione o banco de saída para o pagamento em lote." : "Selecione o banco de entrada para o recebimento em lote."); return;
    }
    setBusy(true); setErr("");
    setBulkProgress({ done: 0, total: selectedItems.length, errors: [] });
    let done = 0;
    const errors = [];
    for (const a of selectedItems) {
      try {
        await api.post("/payments", {
          parent_type: parentType,
          parent_id: a.id,
          valor: Number(a.saldo) || 0,
          data_pagamento: bulkForm.data_pagamento,
          metodo: bulkForm.metodo,
          banco: bulkForm.banco || "",
          observacoes: bulkForm.observacoes,
        });
      } catch (e2) {
        errors.push(`${a.descricao || a.id.slice(0, 8)}: ${formatErr(e2)}`);
      }
      done += 1;
      setBulkProgress({ done, total: selectedItems.length, errors: [...errors] });
    }
    setBusy(false);
    await refreshAll();
    if (errors.length === 0) {
      setBulkOpen(false);
      setSelected(new Set());
      setBulkProgress(null);
    }
  };

  const tone = (acc) => {
    if (acc.status === "paga") return "success";
    if (acc.status === "cancelada") return "neutral";
    if (acc.overdue) return "danger";
    if (acc.status === "parcial") return "warn";
    return "neutral";
  };

  const set = (k) => (e) => setAccForm({ ...accForm, [k]: e.target.value });
  const setPay = (k) => (e) => setPayForm({ ...payForm, [k]: e.target.value });

  return (
    <Layout
      title={title}
      subtitle={subtitle}
      actions={<Button onClick={openNew} data-testid={`new-${kind}-button`}><Plus size={16} /> Lançamento manual</Button>}
    >
      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Em aberto</div>
          <div className="font-mono text-xl font-semibold text-ink-900 mt-1">{fmtBRL(cards.open_value)}</div>
          <div className="text-xs text-ink-500">{cards.open_count} contas</div>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50/30">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-danger">Vencidas</div>
          <div className="font-mono text-xl font-semibold text-danger mt-1">{fmtBRL(cards.overdue_value)}</div>
          <div className="text-xs text-ink-500">{cards.overdue_count} vencidas</div>
        </Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50/30">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-700">Quitadas</div>
          <div className="font-mono text-xl font-semibold text-emerald-700 mt-1">{fmtBRL(cards.paid_value)}</div>
          <div className="text-xs text-ink-500">total acumulado</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Filtrar</div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="mt-1 w-full bg-stone-50 border border-stone-300 rounded-md px-2 py-1 text-sm" data-testid={`${kind}-filter`}>
            <option value="all">Todas</option>
            <option value="open">Em aberto</option>
            <option value="overdue">Vencidas</option>
            <option value="paid">Pagas</option>
            <option value="canceled">Canceladas</option>
          </select>
        </Card>
      </div>

      {/* Search + Categoria */}
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-6">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={`Descrição ou ${cpLabel.toLowerCase()}...`}
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                data-testid={`${kind}-search-input`}
              />
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Categoria</div>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="w-full bg-stone-50 border border-stone-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              data-testid={`${kind}-categoria-filter`}
            >
              <option value="">Todas as categorias</option>
              {isPay ? (
                <>
                  <option value="fornecedor">Produtores / Fornecedores</option>
                  <option value="salario">Funcionários (salários)</option>
                  <option value="salario_adm">Salários ADM</option>
                  <option value="impostos">Impostos</option>
                  <option value="frete">Frete</option>
                  <option value="sacaria">Sacaria</option>
                  <option value="energia">Energia</option>
                  <option value="aluguel">Aluguel</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="materiais_escritorio">Materiais de Escritório</option>
                  <option value="materiais_limpeza">Materiais de Limpeza</option>
                  <option value="taxas_bancarias">Taxas Bancárias</option>
                  <option value="cesta_basica">Cesta Básica</option>
                  <option value="embalagem">Embalagem</option>
                  <option value="outros">Outros / Recorrentes</option>
                </>
              ) : (
                <>
                  <option value="venda">Clientes (vendas)</option>
                  <option value="servico_beneficiamento">Beneficiamento a Terceiros</option>
                  <option value="outros">Outros</option>
                </>
              )}
            </select>
          </div>
          <div className="md:col-span-2 text-right">
            <span className="text-xs text-ink-500">{total} resultado(s)</span>
          </div>
        </div>
      </Card>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Barra de ações em lote */}
      {selectedItems.length > 0 && (
        <div
          className="mb-4 p-4 rounded-lg bg-emerald-50 border border-emerald-300 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          data-testid="bulk-pay-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <Wallet size={18} className="text-emerald-700" />
            <div>
              <div className="font-bold text-emerald-900">
                {selectedItems.length} conta{selectedItems.length > 1 ? "s" : ""} selecionada{selectedItems.length > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-emerald-800">Total a {isPay ? "pagar" : "receber"}: <span className="font-mono font-bold">{fmtBRL(selectedTotal)}</span></div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setSelected(new Set())}
              data-testid="bulk-pay-clear"
            >
              Limpar seleção
            </Button>
            <Button
              onClick={() => {
                setBulkProgress(null);
                setBulkForm({ data_pagamento: new Date().toISOString().slice(0, 10), metodo: "pix", observacoes: "" });
                setBulkOpen(true);
                setErr("");
              }}
              className="!bg-emerald-700 hover:!bg-emerald-800"
              data-testid="bulk-pay-open"
            >
              <Wallet size={16} /> {isPay ? "Pagar selecionadas" : "Receber selecionadas"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : (
        <Card className="overflow-x-auto">
          {items.length === 0 ? (
            <Empty title="Nenhuma conta encontrada" hint="Crie um lançamento manual ou finalize uma operação." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  {(
                    <th className="px-3 py-1 w-1">
                      <input
                        type="checkbox"
                        checked={allEligibleSelected}
                        onChange={toggleAll}
                        title={allEligibleSelected ? "Desmarcar todas" : "Marcar todas em aberto"}
                        className="cursor-pointer"
                        data-testid={`${kind}-select-all`}
                      />
                    </th>
                  )}
                  <th className="px-3 py-1">Descrição</th>
                  <th className="px-3 py-1">{cpLabel}</th>
                  <th className="px-3 py-1">Categoria</th>
                  <th className="px-3 py-1 text-right">Valor</th>
                  <th className="px-3 py-1 text-right">Pago</th>
                  <th className="px-3 py-1 text-right">Saldo</th>
                  <th className="px-3 py-1">Lançamento</th>
                  <th className="px-3 py-1">Vencimento</th>
                  <th className="px-3 py-1">Status</th>
                  <th className="px-3 py-1">Origem</th>
                  <th className="px-3 py-1 w-1">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const isExpanded = expandedId === a.id;
                  return (
                  <Fragment key={a.id}>
                  <tr
                    onClick={() => setExpandedId(isExpanded ? null : a.id)}
                    className={`border-b border-ink-200 last:border-0 transition-colors cursor-pointer ${selected.has(a.id) ? "bg-emerald-50/60" : isExpanded ? "bg-brand-50/40" : "hover:bg-stone-50"}`}
                    data-testid={`${kind}-row-${a.id}`}
                  >
                    {(
                      <td className="px-3 py-0.5" onClick={(e) => e.stopPropagation()}>
                        {isEligible(a) ? (
                          <input
                            type="checkbox"
                            checked={selected.has(a.id)}
                            onChange={() => toggleOne(a.id)}
                            className="cursor-pointer"
                            data-testid={`${kind}-select-${a.id}`}
                          />
                        ) : (
                          <span className="inline-block w-4" />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-0.5 font-medium text-ink-900 text-[13px] max-w-[260px]" title="Clique para ver a descrição completa">
                      <div className="flex items-center gap-1.5">
                        {a.nf_saida_numero && <span className="shrink-0 px-1.5 rounded bg-sky-100 text-sky-800 font-mono text-[11px] font-bold" data-testid={`${kind}-nf-${a.id}`}>NF {a.nf_saida_numero}</span>}
                        <span className="truncate">{a.descricao}</span>
                      </div>
                    </td>
                    <td className="px-3 py-0.5 text-ink-700 text-[13px] max-w-[160px] truncate">{a.counterpart || "—"}</td>
                    <td className="px-3 py-0.5" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={a.categoria || "outros"}
                        onChange={async (e) => {
                          const cat = e.target.value;
                          try {
                            await api.patch(`/finance/accounts-${isPay ? "payable" : "receivable"}/${a.id}/categoria`, { categoria: cat });
                            await load();
                          } catch (err) {
                            alert(formatErr(err));
                          }
                        }}
                        className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border cursor-pointer hover:ring-2 hover:ring-brand-300 focus:ring-2 focus:ring-brand-500 outline-none ${catStyle(a.categoria)}`}
                        title="Clique para trocar a categoria"
                        data-testid={`${kind}-cat-${a.id}`}
                      >
                        {isPay ? (
                          <>
                            <option value="fornecedor">Fornecedor</option>
                            <option value="salario">Salário</option>
                            <option value="salario_adm">Salários ADM</option>
                            <option value="folha">Folha</option>
                            <option value="impostos">Impostos</option>
                            <option value="frete">Frete</option>
                            <option value="sacaria">Sacaria</option>
                            <option value="energia">Energia</option>
                            <option value="aluguel">Aluguel</option>
                            <option value="manutencao">Manutenção</option>
                            <option value="materiais_escritorio">Mat. Escritório</option>
                            <option value="materiais_limpeza">Mat. Limpeza</option>
                            <option value="taxas_bancarias">Taxas Bancárias</option>
                            <option value="cesta_basica">Cesta Básica</option>
                            <option value="embalagem">Embalagem</option>
                            <option value="outros">Outros</option>
                          </>
                        ) : (
                          <>
                            <option value="venda">Venda</option>
                            <option value="servico_beneficiamento">Benef. Terceiros</option>
                            <option value="outros">Outros</option>
                          </>
                        )}
                      </select>
                    </td>
                    <td className="px-3 py-0.5 text-right font-mono text-[13px]">{fmtBRL(a.valor)}</td>
                    <td className="px-3 py-0.5 text-right font-mono text-emerald-700 text-[13px]">{fmtBRL(a.total_pago || 0)}</td>
                    <td className="px-3 py-0.5 text-right font-mono font-semibold text-[13px]">{fmtBRL(a.saldo)}</td>
                    <td className="px-3 py-0.5 font-mono text-[11px] text-ink-500" data-testid={`${kind}-created-${a.id}`}>
                      {a.created_at ? fmtDate(a.created_at.slice(0, 10)) : "—"}
                    </td>
                    <td className="px-3 py-0.5 font-mono text-[13px]">{fmtDate(a.data_vencimento)}</td>
                    <td className="px-3 py-0.5"><Badge tone={tone(a)}>{a.overdue && a.status !== "paga" ? "vencida" : a.status}</Badge></td>
                    <td className="px-3 py-0.5"><Badge tone={a.origem === "auto" ? "neutral" : "warn"}>{a.origem}</Badge></td>
                    <td className="px-3 py-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {a.status !== "paga" && a.status !== "cancelada" && (
                        <button onClick={() => openPay(a)} className="p-1 rounded-md hover:bg-emerald-100 text-emerald-700" title="Registrar pagamento" data-testid={`${kind}-pay-${a.id}`}>
                          <Wallet size={14} />
                        </button>
                      )}
                      {Number(a.total_pago || 0) > 0 && a.status !== "cancelada" && (
                        <button onClick={() => onDesfazerPagamento(a)} className="p-1 rounded-md hover:bg-amber-100 text-amber-700" title="Desfazer pagamento" data-testid={`${kind}-undo-${a.id}`}>
                          <RotateCcw size={14} />
                        </button>
                      )}
                      <button onClick={() => openEditAcc(a)} className="p-1 rounded-md hover:bg-stone-100 text-ink-700" title="Editar valor/vencimento" data-testid={`${kind}-edit-${a.id}`}><Pencil size={14} /></button>
                      {a.origem === "manual" && (
                        <button onClick={() => onDelete(a)} className="p-1 rounded-md hover:bg-red-100 text-danger" title="Excluir"><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${a.id}-detail`} className="bg-brand-50/40 border-b border-ink-200" data-testid={`${kind}-detail-${a.id}`}>
                      <td colSpan={12} className="px-4 py-2 text-[13px] text-ink-800">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">Descrição completa</div>
                            <div className="whitespace-pre-wrap break-words">{a.descricao || "—"}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">{cpLabel}</div>
                            <div className="whitespace-pre-wrap break-words">{a.counterpart || "—"}</div>
                          </div>
                          {a.observacoes && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">Observações</div>
                              <div className="whitespace-pre-wrap break-words text-ink-700">{a.observacoes}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );})}
              </tbody>
            </table>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
            testidPrefix={`${kind}-pagination`}
          />
        </Card>
      )}

      {/* Modal: criar/editar conta manual */}
      {openAcc && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">{editingAcc ? "Editar Conta" : "Novo Lançamento"}</h3>
              <button onClick={() => setOpenAcc(false)} className="text-ink-500"><X size={18} /></button>
            </div>
            <form onSubmit={submitAcc} className="p-6 space-y-4">
              {editingAcc?.origem === "auto" && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  ℹ️ Conta automática: só é possível editar <strong>Valor</strong>, <strong>Vencimento</strong>, <strong>Descrição</strong> e <strong>Observações</strong>. Para trocar categoria use o dropdown na tabela. Para editar fornecedor, altere na fonte (NF, frete, sacaria).
                </div>
              )}
              <Input label="Descrição" required value={accForm.descricao} onChange={set("descricao")} data-testid={`${kind}-desc`} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Valor (R$)" type="number" step="0.01" required value={accForm.valor} onChange={set("valor")} data-testid={`${kind}-valor`} />
                <Input label="Vencimento" type="date" required value={accForm.data_vencimento} onChange={set("data_vencimento")} data-testid={`${kind}-venc`} />
              </div>
              <Input label={cpLabel} value={accForm.counterpart} onChange={set("counterpart")} disabled={editingAcc?.origem === "auto"} />
              <Select label="Categoria" value={accForm.categoria} onChange={set("categoria")} disabled={editingAcc?.origem === "auto"}>
                <option value="outros">Outros</option>
                <option value="frete">Frete</option>
                <option value="sacaria">Sacaria</option>
                <option value="energia">Energia</option>
                <option value="aluguel">Aluguel</option>
                <option value="folha">Folha</option>
                <option value="salario">Salário</option>
                <option value="salario_adm">Salários ADM</option>
                <option value="venda">Venda</option>
                <option value="impostos">Impostos</option>
                <option value="manutencao">Manutenção</option>
                <option value="materiais_escritorio">Materiais de Escritório</option>
                <option value="materiais_limpeza">Materiais de Limpeza</option>
                <option value="taxas_bancarias">Taxas Bancárias</option>
                <option value="cesta_basica">Cesta Básica</option>
                <option value="embalagem">Embalagem</option>
              </Select>
              <Input label="Observações" value={accForm.observacoes} onChange={set("observacoes")} />
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpenAcc(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid={`${kind}-submit`}>{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: pagamento */}
      {paying && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <div>
                <h3 className="font-heading font-bold text-ink-900">{isPay ? "Pagar" : "Receber"} — {paying.descricao}</h3>
                <p className="text-xs text-ink-500">Saldo aberto: <span className="font-mono">{fmtBRL(paying.saldo)}</span></p>
              </div>
              <button onClick={() => setPaying(null)} className="text-ink-500"><X size={18} /></button>
            </div>
            <form onSubmit={submitPay} className="p-6 space-y-4">
              {/* Ação rápida: quitar com desconto */}
              {Number(paying.saldo || 0) > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-amber-900">
                    <strong>{isPay ? "Cliente pediu abatimento?" : "Cliente pagou menos?"}</strong> Aplique um desconto que fecha a conta sem entrada de caixa.
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setPayForm((f) => ({ ...f, valor: String(paying.saldo || 0), metodo: "desconto", banco: "" }))}
                    className="!bg-amber-100 !text-amber-900 hover:!bg-amber-200 !border-amber-300 whitespace-nowrap"
                    data-testid="pay-quitar-desconto"
                  >
                    Quitar com desconto
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input label="Valor" type="number" step="0.01" required value={payForm.valor} onChange={setPay("valor")} data-testid="pay-valor" />
                <Input label="Data" type="date" required value={payForm.data_pagamento} onChange={setPay("data_pagamento")} data-testid="pay-data" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Método" value={payForm.metodo} onChange={setPay("metodo")} data-testid="pay-metodo">
                  {METODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
                {(payForm.metodo === "pix" || payForm.metodo === "transferencia") && (
                  <Select label="Banco" required value={payForm.banco || ""} onChange={setPay("banco")} data-testid="pay-banco">
                    <option value="">Selecione…</option>
                    <option value="sicredi">Sicredi</option>
                    <option value="banco_brasil">Banco do Brasil</option>
                    <option value="safra">Safra</option>
                  </Select>
                )}
              </div>
              {payForm.metodo === "desconto" && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900 space-y-2">
                  <div>
                    <strong>Modo desconto:</strong> este lançamento não movimenta caixa nem banco. Serve apenas para abater o saldo aberto (quitar a conta).
                  </div>
                  {!isPay && paying?.source_type === "shipment" && paying?.source_id && (
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-amber-200">
                      <div className="text-[11px]">
                        <strong>Distribuir desconto nas caixas?</strong> Reajuste os preços por categoria para refletir o abatimento nos relatórios de margem e P&L.
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setPricingShipmentId(paying.source_id)}
                        className="!bg-white !text-amber-900 hover:!bg-amber-100 !border-amber-300 whitespace-nowrap"
                        data-testid="open-pricing-adjust-btn"
                      >
                        Ajustar preços da venda
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <Input label="Observações" value={payForm.observacoes} onChange={setPay("observacoes")} />
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

              {/* histórico */}
              {paying.payments?.length > 0 && (
                <div className="pt-3 border-t border-ink-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">Pagamentos anteriores</div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {paying.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-xs p-2 rounded-md bg-stone-50">
                        <div>
                          <span className="font-mono">{fmtDate(p.data_pagamento)}</span>
                          <span className="ml-2 text-ink-500 capitalize">{p.metodo}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-ink-900">{fmtBRL(p.valor)}</span>
                          <button type="button" onClick={() => onEstornar(p.id)} className="p-1 rounded hover:bg-red-100 text-danger" title="Estornar">
                            <RotateCcw size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setPaying(null)}>Fechar</Button>
                <Button type="submit" disabled={busy} data-testid="pay-submit">{busy ? <Spinner size={16} /> : (isPay ? "Pagar" : "Receber")}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: pagamento em lote */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-pay-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <div>
                <h3 className="font-heading font-bold text-ink-900">{isPay ? "Pagar" : "Receber"} {selectedItems.length} contas</h3>
                <p className="text-xs text-ink-500 mt-0.5">Total: <span className="font-mono font-bold">{fmtBRL(selectedTotal)}</span></p>
              </div>
              <button onClick={() => { if (!busy) setBulkOpen(false); }} className="text-ink-500" disabled={busy}><X size={18} /></button>
            </div>
            <form onSubmit={submitBulkPay} className="p-6 space-y-4">
              <div className="max-h-40 overflow-y-auto border border-ink-200 rounded-md divide-y divide-ink-100 text-xs" data-testid="bulk-pay-list">
                {selectedItems.map((a) => (
                  <div key={a.id} className="flex justify-between items-center px-3 py-1.5">
                    <span className="truncate text-ink-700">{a.descricao}</span>
                    <span className="font-mono font-semibold text-ink-900 whitespace-nowrap ml-3">{fmtBRL(a.saldo)}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label={isPay ? "Data do pagamento" : "Data do recebimento"} type="date" required value={bulkForm.data_pagamento} onChange={(e) => setBulkForm({ ...bulkForm, data_pagamento: e.target.value })} data-testid="bulk-pay-date" />
                <Select label="Método" value={bulkForm.metodo} onChange={(e) => setBulkForm({ ...bulkForm, metodo: e.target.value, banco: "" })} data-testid="bulk-pay-metodo">
                  {METODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </div>
              {(bulkForm.metodo === "pix" || bulkForm.metodo === "transferencia") && (
                <Select label="Banco" required value={bulkForm.banco || ""} onChange={(e) => setBulkForm({ ...bulkForm, banco: e.target.value })} data-testid="bulk-pay-banco">
                  <option value="">Selecione…</option>
                  <option value="sicredi">Sicredi</option>
                  <option value="banco_brasil">Banco do Brasil</option>
                  <option value="safra">Safra</option>
                </Select>
              )}
              <Input label="Observações (aplicadas a todas)" value={bulkForm.observacoes} onChange={(e) => setBulkForm({ ...bulkForm, observacoes: e.target.value })} data-testid="bulk-pay-obs" />

              {bulkProgress && (
                <div className="text-xs">
                  <div className="flex justify-between mb-1">
                    <span className="text-ink-700 font-semibold">Processando…</span>
                    <span className="font-mono text-ink-900">{bulkProgress.done} / {bulkProgress.total}</span>
                  </div>
                  <div className="h-2 bg-stone-200 rounded overflow-hidden">
                    <div className="h-full bg-emerald-600 transition-all" style={{ width: `${(bulkProgress.done / Math.max(1, bulkProgress.total)) * 100}%` }} />
                  </div>
                  {bulkProgress.errors.length > 0 && (
                    <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-red-900 max-h-24 overflow-y-auto">
                      <div className="font-bold mb-1">{bulkProgress.errors.length} erro(s):</div>
                      {bulkProgress.errors.map((msg, i) => <div key={i}>• {msg}</div>)}
                    </div>
                  )}
                </div>
              )}

              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setBulkOpen(false)} disabled={busy}>Cancelar</Button>
                <Button type="submit" disabled={busy || selectedItems.length === 0} className="!bg-emerald-700 hover:!bg-emerald-800" data-testid="bulk-pay-submit">
                  {busy ? <Spinner size={16} /> : <><Wallet size={16} /> {isPay ? "Confirmar pagamento" : "Confirmar recebimento"}</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal: reajuste de preços por categoria (usado após "quitar com desconto" em Venda) */}
      {pricingShipmentId && (
        <PricingAdjustmentModal
          shipmentId={pricingShipmentId}
          defaultMotivo={paying?.descricao ? `Abatimento aplicado em ${paying.descricao}` : ""}
          onClose={() => setPricingShipmentId(null)}
          onSaved={async (newValorBruto) => {
            // Após reajuste, atualiza o modal de pagamento com o novo saldo e sugere quitar
            if (paying) {
              try {
                const r = await api.get(`${url}/${paying.id}`);
                setPaying(r.data);
                setPayForm((f) => ({ ...f, valor: String(r.data.saldo || 0) }));
              } catch (_) { /* ignore */ }
            }
            await refreshAll();
          }}
        />
      )}
    </Layout>
  );
}

export function AccountsPayablePage() { return <AccountsPage kind="payable" />; }
export function AccountsReceivablePage() { return <AccountsPage kind="receivable" />; }
